import { useCallback, useState } from "react";

import { useHistory } from "react-router-dom";

import { useTranslation } from "react-i18next";

import apiClient from "../../../helpers/apiClient";
import { useUser } from "../../../contexts/UserContext/UserContext";
import { setSuccessLogin } from "../../../contexts/UserContext/actions";
import getLoginErrorMessage from "../utils/getLoginErrorMessage";
import { normalizeEmail } from "../utils/normalizeEmail";
import resolvePostLoginRedirect from "../utils/resolvePostLoginRedirect";

const useLogin = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const history = useHistory();
    const { dispatch } = useUser();
    const { t } = useTranslation("login");

    const login = useCallback(
        async ({ email: rawEmail, password: rawPassword }) => {
            setError("");

            const email = normalizeEmail(rawEmail ?? "");
            const password = (rawPassword ?? "").trim();

            if (!email || !password) {
                setError(t("enterEmailAndPassword"));
                return false;
            }

            setLoading(true);

            try {
                const response = await apiClient.post("/api/auth/login", {
                    email,
                    password,
                });

                if (response?.data?.success) {
                    const { user, needsPasswordChange } = response?.data?.data ?? {};

                    if (!user?.id) {
                        setError(t("invalidCredentials"));
                        return false;
                    }

                    dispatch(setSuccessLogin({ user, needsPasswordChange }));

                    await resolvePostLoginRedirect(history);
                    return true;
                }

                setError(response?.data?.message || t("invalidCredentials"));
                return false;
            } catch (err) {
                setError(getLoginErrorMessage(err, t));
                return false;
            } finally {
                setLoading(false);
            }
        },
        [dispatch, history, t],
    );

    return { login, loading, error, setError };
};

export default useLogin;
